import { Injectable, inject } from '@angular/core';
import { Observable, finalize, tap } from 'rxjs';

import { AuthService } from '@core/services/auth.service';

import { AuthSession } from '../../models/auth-session.model';
import { RegisterAccountRequest, RegisterCompleteRequest } from '../../models/register.dto';
import { AuthRepositoryContract } from '../repositories/auth.repository.contract';
import { AuthStore } from '../store/auth.store';
import { apiErrorMessage } from './api-error-message';

/**
 * The two calls of registration, one per screen. Both return their stream
 * rather than subscribing, like LoginUseCase: the page owns step navigation,
 * its in-flight flag and the registrationId handed between the calls; this
 * owns the store and, at the end, the session.
 */
@Injectable()
export class RegisterUseCase {
  private readonly repository = inject(AuthRepositoryContract);
  private readonly store = inject(AuthStore);
  private readonly auth = inject(AuthService);

  /** Step 1 — validates and reserves the account; resolves to the registrationId. */
  createAccount(body: RegisterAccountRequest): Observable<string> {
    return this.track(
      this.repository.registerAccount(body),
      'We could not create your account. Please check your details and try again.',
    );
  }

  /** Step 2 — completes the profile; the visitor is signed in on success. */
  complete(body: RegisterCompleteRequest): Observable<AuthSession> {
    return this.track(
      this.repository.registerComplete(body),
      'We could not finish creating your account. Please try again.',
    ).pipe(
      tap((session) => {
        if (session.token) {
          this.auth.setSession(session.token, session.user);
        }
        this.store.setUser(session.user ?? null);
      }),
    );
  }

  private track<T>(request: Observable<T>, fallback: string): Observable<T> {
    this.store.setLoading(true);
    this.store.setError(null);

    return request.pipe(
      tap({
        error: (err: unknown) => this.store.setError(apiErrorMessage(err, fallback)),
      }),
      finalize(() => this.store.setLoading(false)),
    );
  }
}
